import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { CText } from '../../components/common';
import { FiMic, FiMicOff, FiVideo, FiVideoOff, FiPhoneOff, FiMaximize2, FiMinimize2 } from 'react-icons/fi';

// Mock expert data
const mockExpert = {
  name: 'Dr. Priya Sharma',
  image: null,
};

const CallScreen: React.FC = () => {
  const navigate = useNavigate();
  useParams<{ bookingId: string }>();
  const [searchParams] = useSearchParams();
  const expertName = searchParams.get('expert') || mockExpert.name;

  const colors = useAppSelector((state) => state.theme.theme);
  const user = useAppSelector((state) => state.user.user);

  const [loading, setLoading] = useState(true);
  const [callConnected, setCallConnected] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [sessionTime, setSessionTime] = useState(30 * 60); // 30 minutes
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    // Simulate call connection
    const connectTimer = setTimeout(() => {
      setLoading(false);
      setCallConnected(true);
    }, 2000);

    return () => clearTimeout(connectTimer);
  }, []);

  useEffect(() => {
    if (!callConnected) return;

    // Call duration timer
    const durationTimer = setInterval(() => {
      setCallDuration((prev) => prev + 1);
    }, 1000);

    // Session countdown timer
    const sessionTimer = setInterval(() => {
      setSessionTime((prev) => {
        if (prev <= 0) {
          clearInterval(sessionTimer);
          handleEndCall();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(durationTimer);
      clearInterval(sessionTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callConnected]);

  const formatTime = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleEndCall = () => {
    navigate('/experts');
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const toggleVideo = () => {
    setIsVideoOff(!isVideoOff);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Connecting screen
  if (loading) {
    return (
      <div
        className="flex flex-col items-center justify-center h-screen"
        style={{ backgroundColor: '#1a1a2e' }}
      >
        <div
          className="w-24 h-24 rounded-full flex items-center justify-center mb-6"
          style={{ backgroundColor: colors.primary }}
        >
          <CText type="B32" color={colors.white}>
            {expertName.charAt(0)}
          </CText>
        </div>

        <CText type="B18" color={colors.white} style={{ marginBottom: 8 }}>
          {expertName}
        </CText>

        <CText type="r14" color={colors.grayScale500} style={{ marginBottom: 24 }}>
          Connecting...
        </CText>

        <div className="flex gap-2">
          <div
            className="w-3 h-3 rounded-full animate-pulse"
            style={{ backgroundColor: colors.primary, animationDelay: '0ms' }}
          />
          <div
            className="w-3 h-3 rounded-full animate-pulse"
            style={{ backgroundColor: colors.primary, animationDelay: '150ms' }}
          />
          <div
            className="w-3 h-3 rounded-full animate-pulse"
            style={{ backgroundColor: colors.primary, animationDelay: '300ms' }}
          />
        </div>

        <button
          onClick={handleEndCall}
          className="absolute bottom-10 p-4 rounded-full"
          style={{ backgroundColor: colors.alertColor }}
        >
          {React.createElement(FiPhoneOff as any, { size: 24, color: colors.white })}
        </button>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col h-screen relative"
      style={{ backgroundColor: '#1a1a2e' }}
    >
      {/* Remote Video (Expert) - Full Screen */}
      <div className="flex-1 relative">
        {/* Placeholder for remote video */}
        <div
          className="w-full h-full flex items-center justify-center"
          style={{ backgroundColor: '#2a2a3e' }}
        >
          <div
            className="w-32 h-32 rounded-full flex items-center justify-center"
            style={{ backgroundColor: colors.primary }}
          >
            <CText type="B48" color={colors.white}>
              {expertName.charAt(0)}
            </CText>
          </div>
        </div>

        {/* Expert name overlay */}
        <div className="absolute top-4 left-4">
          <CText type="M16" color={colors.white}>
            {expertName}
          </CText>
        </div>

        {/* Session timer overlay */}
        <div
          className="absolute top-4 right-4 px-3 py-1 rounded-full"
          style={{
            backgroundColor: sessionTime < 300 ? colors.alertColor + '80' : 'rgba(0,0,0,0.5)',
          }}
        >
          <CText type="M12" color={colors.white}>
            {formatTime(sessionTime)} left
          </CText>
        </div>

        {/* Call duration */}
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2">
          <div
            className="px-4 py-1 rounded-full flex items-center gap-2"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          >
            <div
              className="w-2 h-2 rounded-full animate-pulse"
              style={{ backgroundColor: colors.green }}
            />
            <CText type="M14" color={colors.white}>
              {formatTime(callDuration)}
            </CText>
          </div>
        </div>

        {/* Local Video (Self) - Small overlay */}
        <div
          className="absolute bottom-24 right-4 w-28 h-36 rounded-xl overflow-hidden border-2"
          style={{
            borderColor: colors.primary,
            backgroundColor: isVideoOff ? '#3a3a4e' : '#2a2a3e',
          }}
        >
          {isVideoOff ? (
            <div className="w-full h-full flex items-center justify-center">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center"
                style={{ backgroundColor: colors.primary }}
              >
                <CText type="M14" color={colors.white}>
                  {user?.name?.charAt(0) || 'U'}
                </CText>
              </div>
            </div>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <CText type="r12" color={colors.grayScale500}>
                Camera Preview
              </CText>
            </div>
          )}

          {/* Mute indicator on local video */}
          {isMuted && (
            <div
              className="absolute bottom-2 right-2 p-1 rounded-full"
              style={{ backgroundColor: colors.alertColor }}
            >
              {React.createElement(FiMicOff as any, { size: 12, color: colors.white })}
            </div>
          )}
        </div>
      </div>

      {/* Control Buttons */}
      <div
        className="absolute bottom-0 left-0 right-0 p-6"
        style={{
          background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
        }}
      >
        <div className="flex justify-center items-center gap-6">
          {/* Mute Button */}
          <button
            onClick={toggleMute}
            className="p-4 rounded-full"
            style={{
              backgroundColor: isMuted ? colors.alertColor : 'rgba(255,255,255,0.2)',
            }}
          >
            {React.createElement(
              isMuted ? FiMicOff : FiMic as any,
              { size: 24, color: colors.white }
            )}
          </button>

          {/* Video Toggle Button */}
          <button
            onClick={toggleVideo}
            className="p-4 rounded-full"
            style={{
              backgroundColor: isVideoOff ? colors.alertColor : 'rgba(255,255,255,0.2)',
            }}
          >
            {React.createElement(
              isVideoOff ? FiVideoOff : FiVideo as any,
              { size: 24, color: colors.white }
            )}
          </button>

          {/* End Call Button */}
          <button
            onClick={handleEndCall}
            className="p-4 rounded-full"
            style={{ backgroundColor: colors.alertColor }}
          >
            {React.createElement(FiPhoneOff as any, { size: 24, color: colors.white })}
          </button>

          {/* Fullscreen Button */}
          <button
            onClick={toggleFullscreen}
            className="p-4 rounded-full"
            style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
          >
            {React.createElement(
              isFullscreen ? FiMinimize2 : FiMaximize2 as any,
              { size: 24, color: colors.white }
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CallScreen;
